import React, { useState, useEffect } from "react";
import { SmartTooltip, TableAdv, TagAdv, Button } from "../../../../../../components";
import { useIncentiveRoles } from "../IncentiveModuleLayout";
import { dateFormator, mapTableRows } from "../../../../../../utils";
import { fetchIncentiveSheet } from "../../../../../../redux/slices/incentives/incentivesApi";
import dayjs from "dayjs";

const actionColorMap = {
  Paid: "success",
  Unpaid: "danger",
  Pending: "warning",
  Eligible: "indigo",
  Approved: "success",
};

const MasterSheet = () => {
  const { availableRoles, moduleName } = useIncentiveRoles();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // MasterSheet shows all records (Pending and Eligible/Approved pipeline)
        const res = await fetchIncentiveSheet({ module: "NonIT" });
        // Map _id and related fields to strings for TableAdv compatibility
        const formattedRows = (res || []).map(row => {
            const stringId = typeof row._id === 'object' ? `${row._id.placementGrp}_${row._id.invoiceGrp}` : row._id;
            return {
                ...row,
                id: stringId
            };
        });
        setRows(formattedRows);
      } catch (error) {
        console.error("Failed to load master sheet data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleRowToggle = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const columns = [
    {
      key: "consultantName",
      label: "Consultant Name",
      width: "min-w-[200px]! max-w-[250px]!",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.teamMembers && row.teamMembers.length > 0 && (
            <Button
              icon={
                expandedRows[row.id]
                  ? "keyboard_arrow_down"
                  : "keyboard_arrow_right"
              }
              type="icon"
              variant="text"
              size="xs"
              color="black"
              onClick={(e) => {
                e.stopPropagation();
                handleRowToggle(row.id);
              }}
            />
          )}
          <SmartTooltip
            description={row?.consultantName || "N/A"}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1 font-medium">{row?.consultantName || "N/A"}</div>
          </SmartTooltip>
        </div>
      ),
    },
    {
      key: "jobTitle",
      label: "Job Title",
      width: "min-w-[150px]! max-w-[200px]!",
      render: (row) => (
        <SmartTooltip
          description={row?.jobTitle || "N/A"}
          size="xs"
          color="black"
          noicon
          tooltipPosition="bottom"
          portal={true}
        >
          <div className="line-clamp-1">{row?.jobTitle || "N/A"}</div>
        </SmartTooltip>
      ),
    },
    {
      key: "clientName",
      label: "Client",
      width: "min-w-[150px]! max-w-[200px]!",
      render: (row) => (
        <SmartTooltip
          description={row?.clientName || "N/A"}
          size="xs"
          color="black"
          noicon
          tooltipPosition="bottom"
          portal={true}
        >
          <div className="line-clamp-1">{row?.clientName || "N/A"}</div>
        </SmartTooltip>
      ),
    },
    {
      key: "salary",
      label: "Salary",
      render: (row) => `$${row.salary || 0}`,
    },
    {
      key: "totalCommissionPercentage",
      label: "Percentage",
      render: (row) => `${row.totalCommissionPercentage || 0}%`,
    },
    {
      key: "totalCommissionAmount",
      label: "Commission",
      render: (row) => `$${row.totalCommissionAmount || 0}`,
    },
    {
      key: "warrantyEndDate",
      label: "Warranty End Date",
      render: (row) => {
        if (!row.startDate || !row.warrantyPeriod) return "N/A";
        
        try {
          const start = dayjs(row.startDate);
          const period = row.warrantyPeriod.toLowerCase();
          const value = parseInt(period);
          
          if (isNaN(value)) return "N/A";
          
          let endDate;
          if (/month|mnth|mo/i.test(period)) {
            endDate = start.add(value, "month");
          } else if (/day|dy/i.test(period)) {
            endDate = start.add(value, "day");
          } else if (/year|yr/i.test(period)) {
            endDate = start.add(value, "year");
          } else {
            // Fallback: assume months if no unit is found but a value exists
            endDate = start.add(value, "month");
          }
          
          return dateFormator(endDate.toISOString());
        } catch (e) {
          return "N/A";
        }
      }
    },
    {
      key: "startDate",
      label: "Start Date",
      render: (row) => dateFormator(row.startDate),
    },
    {
      key: "endDate",
      label: "End Date",
      render: (row) => dateFormator(row.endDate),
    },
    {
      key: "warrantyPeriod",
      label: "Warranty Period",
      render: (row) => row.warrantyPeriod || "N/A",
    },
    {
      key: "status",
      label: "Status",
      width: "w-[120px]",
      render: (row) => {
        const statuses = [...new Set((row.teamMembers || []).map(m => m.status))];
        const displayStatus = statuses.length === 1 ? statuses[0] : (statuses.includes("Pending") ? "Pending" : "Mixed");
        return (
          <div className="flex items-center">
            <TagAdv
              title={displayStatus}
              size="sm"
              color={actionColorMap[displayStatus] || "gray"}
              colorDepth={600}
              className="w-[100px]! text-center"
            />
          </div>
        );
      },
    },
  ];

  const mappedRows = mapTableRows(rows, columns);

  const getResolvedRoleName = (item) => {
    // NonIT module allows [1, 2, 3]
    const allowedModules = [1, 2, 3];
    const roleId = item.roleId || item.roleType;
    if (!roleId || !availableRoles?.length) return item.roleName || "Unknown";
    
    const role = availableRoles.find(r => String(r.uiid) === String(roleId) || String(r.type) === String(roleId));
    
    if (role && allowedModules.includes(role.module)) {
      return role.name;
    }
    return "Unknown";
  };

  const AccordionComponent = ({ row }) => {
    if (!row.teamMembers || row.teamMembers.length === 0) return null;
    return (
      <div className="bg-[#FFF5EC] p-4 flex flex-col gap-2">
        <div className="flex w-full items-start gap-8 flex-wrap">
          <div className="flex flex-col gap-1 min-w-[max-content]">
            <span className="text-xs font-semibold text-gray-500">
              Invoice Number
            </span>
            <span className="text-sm font-medium text-gray-900">
              {row.invoiceNum || "-"}
            </span>
          </div>
          <div className="flex flex-col gap-1 min-w-[max-content] border-l border-orange-200 pl-4">
            <span className="text-xs font-semibold text-gray-500 pt-1">
              USD Total
            </span>
          </div>

          {row.teamMembers.map((item, idx) => {
            const resolvedRoleName = getResolvedRoleName(item);
            return (
              <div key={idx} className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                  {resolvedRoleName}{" "}
                  <span className="text-gray-500 font-normal">({item.name}) {item.rateUsed}%</span>
                </span>
              <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                {item.amount !== null ? `$${item.amount}` : ""}
                <span className="text-[10px] text-gray-400 font-normal">
                   (${row.totalCommissionAmount || 0} × {item.rateUsed || 0}%)
                </span>
              </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex-1 w-full flex flex-col overflow-hidden">
      <TableAdv
        tableId={"incentive_incentivemodule_nonit_mastersheet"}
        columns={columns}
        rows={mappedRows}
        toolbarRight={{ search: true }}
        accordion={<AccordionComponent />}
        expandedRows={expandedRows}
        onRowToggle={handleRowToggle}
        isLoading={loading}
      />
    </div>
  );
};

export default MasterSheet;
